import { useState, useEffect } from 'react'

type Theme = 'light' | 'dark'

const getSystemTheme = (): Theme => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return 'light'
}

export const useTheme = () => {
  const [theme, setTheme] = useState<Theme>(() => {
    // Always follow system preference by default
    // User can override by toggling, which will save to localStorage
    const savedTheme = localStorage.getItem('theme')
    // Only use saved theme if it was explicitly set by user toggle
    const userSetTheme = localStorage.getItem('theme-user-set')
    if (userSetTheme === 'true' && (savedTheme === 'light' || savedTheme === 'dark')) {
      return savedTheme
    }
    // Follow system preference by default
    return getSystemTheme()
  })

  useEffect(() => {
    // Save theme to localStorage
    localStorage.setItem('theme', theme)
    // Update document class
    document.documentElement.classList.remove('light', 'dark')
    document.documentElement.classList.add(theme)
  }, [theme])

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't manually set a preference
      const userSetTheme = localStorage.getItem('theme-user-set')
      if (userSetTheme !== 'true') {
        setTheme(e.matches ? 'dark' : 'light')
      }
    }
    
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  const toggleTheme = () => {
    // Mark that user explicitly set the theme
    localStorage.setItem('theme-user-set', 'true')
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light')
  }

  // Reset to follow system preference
  const resetToSystem = () => {
    localStorage.removeItem('theme-user-set')
    localStorage.removeItem('theme')
    setTheme(getSystemTheme())
  }

  return { theme, toggleTheme, resetToSystem }
} 